import PropTypes from 'prop-types';
import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import { ThemeProvider } from 'styled-components';

import Table from 'ravenjs/lib/Table';
import TableBody from 'ravenjs/lib/TableBody';
import TableCell from 'ravenjs/lib/TableCell';
import TableHead from 'ravenjs/lib/TableHead';
import TableRow from 'ravenjs/lib/TableRow';
import Typography from 'ravenjs/lib/Typography';
import { genID } from 'ravenjs/utils/generate';
import { getHandler } from 'ravenjs/utils/actions';
import { getMergedThemeFromSchema } from 'ravenjs/utils/theme';
import { getZebraStyle } from 'ravenjs/utils/table';
import { warning } from 'ravenjs/utils/warning';

import DataListStyled from './DataListStyled';

// eslint-disable-next-line valid-jsdoc
/**
 * A simple data-table component.
 */
function DataList(props) {
    const { actions, data, schema, style } = props;
    const colSchema = _get(schema, 'columns', []);
    const rowSchema = _get(schema, 'rows', {});
    const tableProps = _get(schema, 'table', {});
    const tableCellTypes = _get(schema, 'cellTypes', {});
    const orientationMode = _get(schema, 'orientation.mode');
    const sortSchema = _get(schema, 'sorting', {});
    const sortingRef = _get(sortSchema, 'sortingRef', 'onSortChange');
    const sortSchemaActive = Boolean(Object.keys(sortSchema).length);
    const handleSortChange = getHandler(actions, sortingRef);
    const isHeadSortable = headCol =>
        typeof headCol.sortable === 'boolean' ? headCol.sortable : true;
    // Extract the `noData` property,
    // this helps displaying a message to the user if no data is available.
    const noData = _get(schema, 'noData', 'No Data Available');
    // Clean some of the schemas by removing some pre-calculated properties.
    const cleanedRowSchema = _omit(rowSchema, ['zebra']);
    // Throw a warning if no `schema` is provided.
    warning(!_isEmpty(schema), [`A <DataList /> requires a 'schema' object to build the table.`]);
    // Render `null` if no `schema` is provided.
    if (_isEmpty(schema)) {
        return null;
    }
    // Otherwise, continue to render the table.
    return (
        <ThemeProvider theme={getMergedThemeFromSchema(props)}>
            <DataListStyled style={style}>
                <Table cols={colSchema.length || null} {...tableProps}>
                    {orientationMode !== 'portrait' && (
                        <TableHead>
                            <TableRow {...cleanedRowSchema}>
                                {colSchema.map((headCol, headColIdx) => (
                                    <TableCell
                                        actions={headCol.actions || []}
                                        cellProps={{
                                            ...headCol.cellProps,
                                            cellType:
                                                sortSchemaActive && isHeadSortable(headCol)
                                                    ? 'sortable'
                                                    : _get(headCol, 'cellProps.cellType', ''),
                                        }}
                                        col={_omit(headCol, ['actions', 'cellProps'])}
                                        config={headCol}
                                        handlers={actions}
                                        key={genID('DataList_Head_Cell')}
                                        onClick={
                                            sortSchemaActive &&
                                            headCol.id &&
                                            isHeadSortable(headCol)
                                                ? handleSortChange(
                                                      sortSchema.order,
                                                      headCol.sortId || headCol.id
                                                  )
                                                : null
                                        }
                                        sortSchema={sortSchema}
                                        type="head"
                                    >
                                        {headCol.title}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {(!data || data.length === 0) && (
                            <TableRow {...cleanedRowSchema}>
                                <TableCell colSpan={colSchema.length} noDataAvailable>
                                    <Typography
                                        align="center"
                                        gutterBottom="0"
                                        gutterTop="0"
                                        type="subheading"
                                    >
                                        {noData}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {data &&
                            data.length > 0 &&
                            data.map((row, rowIdx) => (
                                <TableRow
                                    {...rowSchema}
                                    handlers={actions}
                                    key={genID(`DataList_Body_Row_${rowIdx}_`)}
                                    row={row}
                                    schema={schema}
                                    zebra={getZebraStyle(rowIdx, rowSchema.zebra)}
                                >
                                    {colSchema.map((bodyCol, bodyColIdx) => (
                                        <TableCell
                                            actions={bodyCol.actions || []}
                                            cellProps={bodyCol.cellProps || {}}
                                            cellTypes={tableCellTypes}
                                            col={_omit(bodyCol, ['actions', 'cellProps'])}
                                            data-title={bodyCol.title || ''}
                                            handlers={actions}
                                            key={genID(`DataList_Body_Cell_${bodyCol.id}_`)}
                                            row={row}
                                            schema={schema}
                                        >
                                            <Typography
                                                noWrap
                                                gutterBottom="0"
                                                title={_get(row, bodyCol.id, '-')}
                                            >
                                                {_get(row, bodyCol.id, '-')}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </DataListStyled>
        </ThemeProvider>
    );
}

DataList.propTypes = {
    /**
     * A map of action handlers used by rendered components.
     */
    actions: PropTypes.object,
    /**
     * The data for the `DataList`.
     */
    data: PropTypes.array,
    /**
     * The schema for the `DataList`.
     */
    schema: PropTypes.object,
    /**
     * The style for the `DataList`.
     */
    style: PropTypes.object,
};

DataList.defaultProps = {
    actions: {},
    data: [],
    schema: {},
    style: {},
};

export default DataList;
